import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ApplicationDTO, ProjectDetailDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, EmptyState, Field, Panel, inputClass } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { canContribute, canManage, useApplications, useProjectRole } from "../project-data";

/** APPS dle /Design: karty aplikací s design verzemi a vazbou na codebases. */
export function AppsTab({ project }: { project: ProjectDetailDTO }) {
  const applications = useApplications(project.id);
  const role = useProjectRole(project);
  const [creating, setCreating] = useState(false);
  const apps = applications.data?.applications ?? [];

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="af-label-dim">{cs.apps.header}</div>
        {canManage(role) ? (
          <Btn onClick={() => setCreating(true)} data-testid="app-new">
            {cs.apps.newApp}
          </Btn>
        ) : null}
      </div>

      {creating ? (
        <NewAppForm project={project} onClose={() => setCreating(false)} />
      ) : null}

      {apps.length === 0 && !creating ? (
        <Panel className="mt-4 p-8">
          <EmptyState title={cs.apps.empty} note={cs.apps.emptyNote} />
        </Panel>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {apps.map((a) => (
            <AppCard
              key={a.id}
              app={a}
              projectId={project.id}
              contributor={canContribute(role)}
              manager={canManage(role)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AppCard({
  app,
  projectId,
  contributor,
  manager,
}: {
  app: ApplicationDTO;
  projectId: string;
  contributor: boolean;
  manager: boolean;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["section", projectId, "applications"] });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadDesign(app.id, file),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });
  const setCurrentMutation = useMutation({
    mutationFn: (versionId: string) => api.setDesignCurrent(app.id, versionId),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteApplication(app.id),
    onSuccess: invalidate,
  });

  return (
    <Panel className="flex flex-col gap-2.5 p-4" data-testid="app-card">
      <div className="flex items-center justify-between gap-2">
        <span className="af-display min-w-0 break-words text-[14px] font-semibold" style={{ color: "var(--text-primary)" }}>
          {app.name}
        </span>
        {manager ? (
          <button
            type="button"
            className="cursor-pointer text-[11px]"
            style={{ color: "var(--critical)" }}
            title="Smazat aplikaci"
            onClick={() => {
              if (confirm(`Smazat aplikaci ${app.name} včetně design verzí?`)) deleteMutation.mutate();
            }}
          >
            ✕
          </button>
        ) : null}
      </div>
      <div className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
        {app.description || "—"}
      </div>
      <div className="text-[11px]" style={{ color: "var(--accent)" }}>
        {cs.apps.codebase}{" "}
        {app.codebases.length ? app.codebases.map((c) => c.slug).join(", ") : cs.apps.noCodebase}
      </div>

      <div className="pt-2.5" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <div className="af-label-dim mb-2">{cs.apps.designVersions}</div>
        {app.designVersions.length === 0 ? (
          <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.apps.noDesign}
          </div>
        ) : (
          app.designVersions.map((v) => (
            <div
              key={v.id}
              className="mb-1.5 flex items-center justify-between gap-2 px-2.5 py-1.5"
              style={{ borderRadius: "var(--radius-box)",
                border: `1px solid ${v.isCurrent ? "color-mix(in srgb, var(--positive) 35%, transparent)" : "var(--border-subtle)"}`,
                background: v.isCurrent ? "color-mix(in srgb, var(--positive) 3%, transparent)" : "transparent",
              }}
              data-testid="design-version"
            >
              <span className="min-w-0 break-words text-[11px]" style={{ color: "var(--text-primary)" }}>
                {v.label ?? `v${v.version}`} · {v.filename}
              </span>
              <span className="flex shrink-0 items-center gap-2 text-[11px]">
                <span style={{ color: "var(--text-tertiary)" }}>{relativeTime(v.createdAt)}</span>
                {v.isCurrent ? (
                  <span style={{ color: "var(--positive)" }}>{cs.apps.current}</span>
                ) : manager ? (
                  <button
                    type="button"
                    className="cursor-pointer"
                    style={{ color: "var(--accent)" }}
                    onClick={() => setCurrentMutation.mutate(v.id)}
                  >
                    {cs.apps.setCurrent}
                  </button>
                ) : null}
              </span>
            </div>
          ))
        )}
        {contributor ? (
          <>
            <button
              type="button"
              className="mt-1 w-full cursor-pointer px-2 py-2 text-center text-[11px]"
              style={{ border: "1px dashed color-mix(in srgb, var(--accent) 20%, transparent)", color: "var(--text-tertiary)" }}
              onClick={() => fileRef.current?.click()}
              disabled={uploadMutation.isPending}
              data-testid="design-upload"
            >
              {uploadMutation.isPending ? cs.common.loading : cs.apps.uploadZip}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".zip,application/zip"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadMutation.mutate(file);
                e.target.value = "";
              }}
            />
          </>
        ) : null}
        {error ? (
          <div className="mt-1 text-[11px]" style={{ color: "var(--critical)" }}>
            ⚠ {error}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

function NewAppForm({ project, onClose }: { project: ProjectDetailDTO; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.createApplication(project.id, { name, description }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["section", project.id, "applications"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <Panel brackets className="mt-4 max-w-lg p-4" data-testid="app-new-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={cs.fields.name}>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="app-new-name"
          />
        </Field>
        <Field label={cs.fields.description}>
          <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
      {error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <Btn onClick={onClose}>{cs.common.cancel}</Btn>
        <Btn
          variant="primary"
          disabled={name.trim().length < 2 || mutation.isPending}
          onClick={() => mutation.mutate()}
          data-testid="app-new-save"
        >
          {cs.common.create}
        </Btn>
      </div>
    </Panel>
  );
}
